import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CHANGE_DIRECTION, CHANGE_TYPE, NumberWithChangeConfig } from '@vendasta/galaxy/number-with-change';
import { DisplayValueDescriptor } from '@vendasta/galaxy/pipes';
import { MetricCardEmptyState, MetricCardHeader } from './interface';

import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { VaGoalTableModule } from '@vendasta/uikit';
import { GalaxyNumberWithChangeModule } from '@vendasta/galaxy/number-with-change';
import { DateRangeSelectorModule } from '../../date-range-selector';
import { LoadingStateCardComponent } from '../../loading-state-card/loading-state-card.component';

@Component({
  selector: 'app-metrics-card',
  templateUrl: './metrics-card.component.html',
  styleUrls: ['./metrics-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatCardModule,
    TranslateModule,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    DateRangeSelectorModule,
    NgxChartsModule,
    VaGoalTableModule,
    LoadingStateCardComponent,
    GalaxyNumberWithChangeModule,
    MatButtonModule,
  ],
})
export class MetricsCardComponent {
  @Input() header: MetricCardHeader;
  @Input() formatValueDisplayFunc: (DisplayValueDescriptor) => DisplayValueDescriptor;
  @Input() hideTopStats = false;
  @Input() reverseTrend = false;
  @Input() cardSize: string;
  @Input() isEmpty = false;
  @Input() emptyState: MetricCardEmptyState;
  @Input() graphName?: string;
  @Input() cardImage?: string;
  @Input() currentAgeText?: string;
  @Input() set currentPeriod(currentPeriod: DisplayValueDescriptor) {
    this.current = currentPeriod;
    this.calculateChanges();
  }
  @Input() set previousPeriod(previousPeriod: DisplayValueDescriptor) {
    this.previous = previousPeriod;
    this.calculateChanges();
  }
  current: DisplayValueDescriptor;
  changes: NumberWithChangeConfig;
  previous: DisplayValueDescriptor;

  calculateChanges(): void {
    this.current.value =
      typeof this.current.value === 'string' ? this.clearString(this.current.value) : this.current.value;
    if (this.previous !== undefined && this.previous.value !== undefined) {
      this.previous.value =
        typeof this.previous.value === 'string' ? this.clearString(this.previous.value) : this.previous.value;
      const diff = Number(this.current.value) - Number(this.previous.value);
      const changeDirection = this.calcMetricTrend(diff);
      const changeType = this.reverseTrend ? this.calcMetricTrendType(-diff) : this.calcMetricTrendType(diff);
      const prev = {
        value: Math.abs(diff),
        displayType: 'number',
      } as DisplayValueDescriptor;
      this.changes = {
        currentPeriod: this.formatValueDisplayFunc
          ? this.truncate(this.formatValueDisplayFunc(this.current))
          : this.truncate(this.current),
        previousPeriod: this.formatValueDisplayFunc
          ? this.truncate(this.formatValueDisplayFunc(prev))
          : this.truncate(prev),
        previousPeriodChangeDirection: changeDirection,
        previousPeriodChangeType: changeType,
      } as NumberWithChangeConfig;
    } else {
      this.changes = {
        currentPeriod: this.formatValueDisplayFunc
          ? this.truncate(this.formatValueDisplayFunc(this.current))
          : this.truncate(this.current),
      } as NumberWithChangeConfig;
    }
  }

  private calcMetricTrend(change: number): number {
    const trends = [CHANGE_DIRECTION.DOWNWARD_CHANGE, CHANGE_DIRECTION.NO_CHANGE, CHANGE_DIRECTION.UPWARD_CHANGE];
    return trends[Math.sign(change) + 1];
  }

  private calcMetricTrendType(change: number): number {
    const trends = [CHANGE_TYPE.NEGATIVE_CHANGE, CHANGE_TYPE.NO_CHANGE, CHANGE_TYPE.POSITIVE_CHANGE];
    return trends[Math.sign(change) + 1];
  }

  private clearString(str: string): string {
    str = str.replace(/%/g, '');
    if (str.search(':') !== -1 && str.match(/:/g).length === 2) {
      const arr = str.split(':');
      str = String(this.fixNumber(Number(arr[0]) * 3600 + Number(arr[1]) * 60 + Number(arr[2])));
    }
    return str;
  }

  private truncate(value: DisplayValueDescriptor): DisplayValueDescriptor {
    switch (value.displayType) {
      case 'string': {
        const valueStr = String(value.value);
        if (value.value[valueStr.length - 1] === '%') {
          value.value = String(this.fixNumber(Number(valueStr.replace(/%/g, '')))) + '%';
        } else if (!isNaN(Number(valueStr))) {
          value.value = String(this.fixNumber(Number(valueStr)));
        }
        break;
      }
      case 'number':
        value.value = this.fixNumber(Number(value.value));
        break;
    }
    return value;
  }

  private fixNumber(value: number): number {
    const valueInt = value.toFixed();
    const valueFloat = value.toFixed(2);
    if (valueInt === valueFloat) {
      // if an intended integer number became a long float number
      value = Number(valueInt);
    } else {
      // if an intended float number has more than two digits after floating point
      value = Number(valueFloat);
    }
    return value;
  }
}
